import {
   API_FAILED,
   API_FETCHING,
   API_INITIAL,
   API_SUCCESS,
   APIStatus
} from '@flowwai-frontend/shared/api-common'
import {
   MutationFunction,
   useMutation,
   UseMutationOptions
} from '@tanstack/react-query'

type Props<TData, TError, TVariables> = Omit<
   UseMutationOptions<TData, TError, TVariables>,
   'mutationFn'
>

interface Callbacks<TData, TError> {
   onSuccess?: (data: TData) => void
   onError?: (error: TError) => void
}

const useAsyncWithAuth = <TData, TError extends Error, TRequestObj>(
   getDataAPI: MutationFunction<TData, TRequestObj>,
   props?: Props<TData, TError, TRequestObj>
) => {
   const { mutate, mutateAsync, ...other } = useMutation(getDataAPI, props)

   const getAPIStatus = (): APIStatus => {
      const { isLoading, isError, isSuccess } = other

      if (isLoading) return API_FETCHING
      if (isSuccess) return API_SUCCESS
      if (isError) return API_FAILED

      return API_INITIAL
   }

   const triggerAsyncAPI = async (
      requestObject: TRequestObj,
      callbacks: Callbacks<TData, TError>
   ) => {
      const { onError, onSuccess } = callbacks
      let data

      try {
         await mutateAsync(requestObject).then((response: TData) => {
            data = response

            onSuccess && onSuccess(response)
         })
         return data
      } catch (error) {
         onError && onError(error as any) //TODO: Need to handle types properly here
      }
      return data
   }

   const triggerAsyncAPIWithError = async (
      requestObject: TRequestObj,
      callbacks: Callbacks<TData, TError>
   ) => {
      const { onError, onSuccess } = callbacks

      try {
         const response = await mutateAsync(requestObject)
         if (onSuccess) onSuccess(response)
         return response
      } catch (error) {
         onError && onError(error as any) //TODO: Need to handle types properly here
         throw error
      }
   }

   return {
      ...other,
      triggerAPI: mutate,
      triggerAsyncAPI: triggerAsyncAPI,
      triggerAsyncAPIWithError: triggerAsyncAPIWithError,
      mutateAsync,
      apiStatus: getAPIStatus()
   }
}

export { useAsyncWithAuth }
